import { useEffect, useState } from "react";
import { profileApi, AdminConfigPayload, AdminConfigPatch } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { useDemoCode } from "../auth/DemoCodeProvider";
import { getRuntimeConfig } from "../runtime-config";

interface ProfileSummary {
  responseStyle: string;
  detailLevel: string;
  format: string;
  expertise: Record<string, string>;
  explicitPreferences: string[];
  topicsOfInterest: string[];
  conversationCount: number;
  createdAt: string;
  lastActiveAt: string;
}

export function SettingsPanel({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const { code: demoCode, setCode: setDemoCode } = useDemoCode();
  const cfg = getRuntimeConfig();
  const api = user ? profileApi(user.idToken) : null;

  const [profile, setProfile] = useState<ProfileSummary | null>(null);
  const [usage, setUsage] = useState<{ singleAgent: number; quick: number; deep: number } | null>(null);
  const [admin, setAdmin] = useState<AdminConfigPayload | null>(null);

  // Form state for the admin-only panel — each section saves independently
  // so a partial config edit can't accidentally roll back another section.
  const [turnLimitDraft, setTurnLimitDraft] = useState<string>("");
  const [inviteCodeDraft, setInviteCodeDraft] = useState<string>("");
  const [quotaUserDraft, setQuotaUserDraft] = useState<string>("");
  const [quotaGlobalDraft, setQuotaGlobalDraft] = useState<string>("");
  const [adminSaving, setAdminSaving] = useState<string | null>(null);  // section name while saving
  const [adminErr, setAdminErr] = useState<string | null>(null);

  // Demo code input for non-admin users
  const [demoCodeDraft, setDemoCodeDraft] = useState<string>(demoCode ?? "");

  const [newPref, setNewPref] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!api) return;
    Promise.all([api.get(), api.usage(), api.getAdminConfig()])
      .then(([p, u, a]) => {
        setProfile(p as ProfileSummary);
        setUsage(u);
        setAdmin(a);
        setTurnLimitDraft(String(a.turnLimit));
        setInviteCodeDraft(a.inviteCode ?? "");
        setQuotaUserDraft(a.dailyDebateQuotaUser === null ? "" : String(a.dailyDebateQuotaUser));
        setQuotaGlobalDraft(a.dailyDebateQuotaGlobal === null ? "" : String(a.dailyDebateQuotaGlobal));
      })
      .catch((e) => setErr(String(e)));
  }, []);

  async function saveAdminPatch(section: string, patch: AdminConfigPatch) {
    if (!api) return;
    setAdminErr(null);
    setAdminSaving(section);
    try {
      // PUT returns the full payload (same shape as GET) — use it directly.
      // A follow-up GET would race AgentCore Memory's eventually-consistent
      // ListMemoryRecords and frequently return the pre-write value, which
      // is why "saved value didn't budge in the UI" was happening before.
      const fresh = await api.setAdminConfig(patch);
      setAdmin(fresh);
      setTurnLimitDraft(String(fresh.turnLimit));
      setInviteCodeDraft(fresh.inviteCode ?? "");
      setQuotaUserDraft(fresh.dailyDebateQuotaUser === null ? "" : String(fresh.dailyDebateQuotaUser));
      setQuotaGlobalDraft(fresh.dailyDebateQuotaGlobal === null ? "" : String(fresh.dailyDebateQuotaGlobal));
    } catch (e) {
      setAdminErr(String(e));
    } finally {
      setAdminSaving(null);
    }
  }

  async function toggleDemoMode() {
    if (!admin) return;
    await saveAdminPatch("demoMode", { demoModeEnabled: !admin.demoModeEnabled });
  }

  async function saveTurnLimit() {
    const n = Number(turnLimitDraft);
    if (!Number.isFinite(n)) return;
    await saveAdminPatch("turnLimit", { turnLimit: n });
  }

  async function saveInviteCode() {
    const v = inviteCodeDraft.trim();
    await saveAdminPatch("inviteCode", { inviteCode: v.length > 0 ? v : null });
  }

  async function saveQuotaUser() {
    const v = quotaUserDraft.trim();
    await saveAdminPatch("quotaUser", { dailyDebateQuotaUser: v === "" ? null : Number(v) });
  }

  async function saveQuotaGlobal() {
    const v = quotaGlobalDraft.trim();
    await saveAdminPatch("quotaGlobal", { dailyDebateQuotaGlobal: v === "" ? null : Number(v) });
  }

  async function addPref() {
    if (!api || !profile || !newPref.trim()) return;
    const next = [...profile.explicitPreferences, newPref.trim()];
    await api.setExplicitPreferences(next);
    setProfile({ ...profile, explicitPreferences: next });
    setNewPref("");
  }
  async function removePref(i: number) {
    if (!api || !profile) return;
    const next = profile.explicitPreferences.filter((_, idx) => idx !== i);
    await api.setExplicitPreferences(next);
    setProfile({ ...profile, explicitPreferences: next });
  }
  async function exportJson() {
    if (!api) return;
    const blob = await api.export();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${cfg.appName}-profile.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
  async function deleteAll() {
    if (!api) return;
    if (!confirm("Delete your entire profile? This cannot be undone.")) return;
    await api.delete();
    setProfile(null);
  }
  async function resetAgent(agent: string) {
    if (!api) return;
    if (!confirm(`Reset ${agent} conversation history?`)) return;
    await api.resetAgentHistory(agent);
  }
  async function resetAll() {
    if (!api) return;
    if (!confirm("Reset all conversation history (keeps your profile)?")) return;
    await api.resetAllHistory();
  }

  function applyDemoCode() {
    const v = demoCodeDraft.trim();
    setDemoCode(v.length > 0 ? v : null);
  }
  function clearDemoCode() {
    setDemoCodeDraft("");
    setDemoCode(null);
  }

  return (
    <div className="modal" role="dialog" aria-modal>
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__body">
        <div className="modal__header">
          <h2>Settings</h2>
          <button onClick={onClose} aria-label="Close">✕</button>
        </div>
        {err && <div className="error">{err}</div>}

        {!profile && !err && <div>Loading…</div>}

        {profile && (
          <>
            <section>
              <h3>Your inferred profile</h3>
              {profile.conversationCount < 3
                ? <p className="hint">Your profile builds automatically as you chat — after a few conversations, the agents will start tailoring responses to how you like them.</p>
                : (
                  <ul className="profile-list">
                    <li>Response style: <strong>{profile.responseStyle}</strong></li>
                    <li>Detail level: <strong>{profile.detailLevel}</strong></li>
                    <li>Preferred format: <strong>{profile.format}</strong></li>
                    {Object.keys(profile.expertise).length > 0 && (
                      <li>Expertise: {Object.entries(profile.expertise).map(([k, v]) => `${k} (${v})`).join(", ")}</li>
                    )}
                    {profile.topicsOfInterest.length > 0 && (
                      <li>Topics of interest: {profile.topicsOfInterest.join(", ")}</li>
                    )}
                  </ul>
                )}
            </section>

            <section>
              <h3>Your explicit preferences</h3>
              <p className="hint">These always override inferred preferences.</p>
              <ul className="prefs">
                {profile.explicitPreferences.map((p, i) => (
                  <li key={i}>{p} <button onClick={() => removePref(i)}>✕</button></li>
                ))}
              </ul>
              <form onSubmit={(e) => { e.preventDefault(); addPref(); }}>
                <input value={newPref} onChange={(e) => setNewPref(e.target.value)}
                  placeholder="e.g. Prefer bullet points for technical answers" />
                <button type="submit" disabled={!newPref.trim()}>Add</button>
              </form>
            </section>

            <section>
              <h3>Daily usage</h3>
              {usage && (
                <ul>
                  <li>Single-agent messages: {usage.singleAgent} / {cfg.rateLimits.singleAgentMessagesPerDay}</li>
                  <li>Quick Collaborate: {usage.quick} / {cfg.rateLimits.quickCollaboratePerDay}</li>
                  <li>Deep Debate: {usage.deep} / {cfg.rateLimits.deepDebatePerDay}</li>
                </ul>
              )}
            </section>

            {admin && !admin.isAdmin && (
              <section>
                <h3>Demo invite code</h3>
                <p className="hint">
                  Paste the invite code shared with you. Stored only in this tab's memory —
                  you'll need to re-enter after a page refresh.
                </p>
                <form
                  onSubmit={(e) => { e.preventDefault(); applyDemoCode(); }}
                  style={{ display: "flex", gap: 8, alignItems: "center" }}
                >
                  <input
                    type="text"
                    value={demoCodeDraft}
                    onChange={(e) => setDemoCodeDraft(e.target.value)}
                    placeholder="Demo code"
                    style={{ flex: 1 }}
                  />
                  <button type="submit" disabled={!demoCodeDraft.trim() || demoCodeDraft.trim() === demoCode}>
                    Apply
                  </button>
                  {demoCode && <button type="button" onClick={clearDemoCode}>Clear</button>}
                </form>
                {demoCode && <p className="hint">Active code: <strong>{demoCode}</strong></p>}
              </section>
            )}

            <section>
              <h3>Reset conversation history</h3>
              <div className="btn-row">
                {["claude", "gemini", "grok", "collaborator"].map((a) => (
                  <button key={a} onClick={() => resetAgent(a)}>Reset {a}</button>
                ))}
                <button onClick={resetAll}>Reset all</button>
              </div>
              <p className="hint">This does not affect your profile.</p>
            </section>

            <section>
              <h3>Your data</h3>
              <div className="btn-row">
                <button onClick={exportJson}>Export profile as JSON</button>
                <button className="danger" onClick={deleteAll}>Delete entire profile</button>
              </div>
              <p className="hint">
                Data retention: profile is retained while you're active. After 30 days of inactivity,
                we flag it for deletion and email you 30 days before it's removed. Deletion is immediate
                from your perspective and fully purged from our systems within 24 hours.
              </p>
            </section>

            {admin && (
              <section>
                <h3>{admin.isAdmin ? "Admin — session settings" : "Session settings"}</h3>
                <ul className="profile-list">
                  <li>
                    Current turn limit per chat session: <strong>{admin.turnLimit}</strong>
                    {" "}<span className="hint">(default {admin.defaults.turnLimit}, max {admin.defaults.maxTurnLimit})</span>
                  </li>
                </ul>
                {admin.isAdmin && (
                  <form
                    onSubmit={(e) => { e.preventDefault(); saveTurnLimit(); }}
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <label htmlFor="turnLimitInput">New limit:</label>
                    <input
                      id="turnLimitInput"
                      type="number"
                      min={1}
                      max={admin.defaults.maxTurnLimit}
                      value={turnLimitDraft}
                      onChange={(e) => setTurnLimitDraft(e.target.value)}
                      style={{ width: 80 }}
                    />
                    <button
                      type="submit"
                      disabled={adminSaving === "turnLimit" || !turnLimitDraft || Number(turnLimitDraft) === admin.turnLimit}
                    >
                      {adminSaving === "turnLimit" ? "Saving…" : "Save"}
                    </button>
                  </form>
                )}
                <p className="hint">
                  After this many user messages in a session, the agent stops accepting new
                  messages until the user clicks "End Session" to start a fresh one.
                  {admin.isAdmin
                    ? " Changes here apply to every user."
                    : " Only the admin can change this value."}
                </p>
              </section>
            )}

            {admin && admin.isAdmin && (
              <section>
                <h3>Admin — demo gating</h3>
                <p className="hint">
                  Cost control before Bedrock. You bypass these gates as admin — they affect other users.
                </p>

                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
                  <strong>Demo mode:</strong>
                  <span style={{ color: admin.demoModeEnabled ? "#5af" : "#f55" }}>
                    {admin.demoModeEnabled ? "ON" : "OFF (chat returns 503 to non-admin)"}
                  </span>
                  <button
                    onClick={toggleDemoMode}
                    disabled={adminSaving === "demoMode"}
                  >
                    {adminSaving === "demoMode"
                      ? "Saving…"
                      : admin.demoModeEnabled ? "Disable demo" : "Enable demo"}
                  </button>
                </div>

                <form
                  onSubmit={(e) => { e.preventDefault(); saveInviteCode(); }}
                  style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}
                >
                  <label htmlFor="inviteCodeInput" style={{ minWidth: 140 }}>Invite code:</label>
                  <input
                    id="inviteCodeInput"
                    type="text"
                    value={inviteCodeDraft}
                    onChange={(e) => setInviteCodeDraft(e.target.value)}
                    placeholder="(empty = open access)"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="submit"
                    disabled={adminSaving === "inviteCode" || inviteCodeDraft.trim() === (admin.inviteCode ?? "")}
                  >
                    {adminSaving === "inviteCode" ? "Saving…" : "Save code"}
                  </button>
                </form>

                <form
                  onSubmit={(e) => { e.preventDefault(); saveQuotaUser(); }}
                  style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}
                >
                  <label htmlFor="quotaUserInput" style={{ minWidth: 140 }}>Per-user daily:</label>
                  <input
                    id="quotaUserInput"
                    type="number"
                    min={0}
                    value={quotaUserDraft}
                    onChange={(e) => setQuotaUserDraft(e.target.value)}
                    placeholder={`(default ${cfg.rateLimits.deepDebatePerDay})`}
                    style={{ width: 100 }}
                  />
                  <button
                    type="submit"
                    disabled={adminSaving === "quotaUser"}
                  >
                    {adminSaving === "quotaUser" ? "Saving…" : "Save"}
                  </button>
                  <span className="hint">
                    Currently: <strong>{admin.dailyDebateQuotaUser ?? "default"}</strong>
                  </span>
                </form>
                <p className="hint" style={{ marginTop: 0, marginBottom: 16 }}>
                  Note: this slot is for a future global per-user cap. Per-mode caps from <code>app.config</code>
                  (single-agent / quick / deep) still enforce on every request.
                </p>

                <form
                  onSubmit={(e) => { e.preventDefault(); saveQuotaGlobal(); }}
                  style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}
                >
                  <label htmlFor="quotaGlobalInput" style={{ minWidth: 140 }}>Global daily:</label>
                  <input
                    id="quotaGlobalInput"
                    type="number"
                    min={0}
                    value={quotaGlobalDraft}
                    onChange={(e) => setQuotaGlobalDraft(e.target.value)}
                    placeholder="(empty = no cap)"
                    style={{ width: 100 }}
                  />
                  <button
                    type="submit"
                    disabled={adminSaving === "quotaGlobal"}
                  >
                    {adminSaving === "quotaGlobal" ? "Saving…" : "Save"}
                  </button>
                  <span className="hint">
                    Today so far: <strong>{admin.globalUsageToday}</strong>
                    {admin.dailyDebateQuotaGlobal !== null && ` / ${admin.dailyDebateQuotaGlobal}`}
                  </span>
                </form>
                <p className="hint">
                  Resets at next UTC midnight ({new Date(admin.resetAt).toLocaleString()}).
                  When the global cap is hit, all chat routes return 503 — even for users under their per-user quota.
                </p>

                {adminErr && <div className="error">{adminErr}</div>}
              </section>
            )}

            <footer className="modal__foot">
              <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy</a>
              {" · "}
              <a href="/terms.html" target="_blank" rel="noreferrer">Terms</a>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
